import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from './admin/useAdminApi';

/**
 * The remit@ queue — remittance advices customers email us when they pay by
 * bank bill-pay, their own ACH, or a mailed check.
 *
 * ⚠ Nothing here books a payment. QuickBooks applies the money; a row goes
 * `cleared` only once the mirror shows its invoices at zero.
 */

export type RemitStatus = 'received' | 'open' | 'cleared' | 'needs_review' | 'rejected' | 'failed';

export interface RemitInvoiceLine {
  invoice_number: string;
  amount: number | null;
  qbo_invoice_id: string | null;
  doc_number: string | null;
  matched: boolean;
  balance: number | null;
  paid: boolean;
  ambiguous?: boolean;
}

export interface RemittanceRow {
  id: string;
  from_email: string | null;
  from_name: string | null;
  subject: string | null;
  received_at: string | null;
  status: RemitStatus;
  disposition: string | null;
  customer_id: string | null;
  customer_name: string | null;
  identity_note: string | null;
  candidates: Array<{ customerId: string; name: string; why: string }> | null;
  payer_name: string | null;
  payment_method: string | null;
  payment_ref: string | null;
  payment_date: string | null;
  amount_total: number | null;
  invoices: RemitInvoiceLine[] | null;
  unmatched: string[] | null;
  attachment_name: string | null;
  attachment_url?: string | null;
  raw_text?: string | null;
  cleared_at: string | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  notes: string | null;
}

export function useRemittances(status?: RemitStatus | 'all') {
  const qs = status && status !== 'all' ? `?status=${status}` : '';
  return useQuery<{ ok: true; remittances: RemittanceRow[]; cleared_now?: number }>({
    queryKey: ['remittances', status ?? 'all'],
    queryFn: () => adminFetch(`admin-remittances${qs}`),
    staleTime: 30 * 1000,
  });
}

export function useRemittance(id: string | null) {
  return useQuery<{
    ok: true;
    /* The endpoint's ?id= branch returns the row PLUS the two things the list
       cannot carry: the email body, and a short-lived signed URL for the
       attachment. Without them an unreadable advice has nothing to look at. */
    remittance: RemittanceRow & { raw_text?: string | null; subject?: string | null };
    document_url?: string | null;
  }>({
    queryKey: ['remittance', id],
    queryFn: () => adminFetch(`admin-remittances?id=${id}`),
    enabled: !!id,
  });
}

export function useRemittanceAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      action: 'assign' | 'acknowledge' | 'dismiss' | 'reprocess' | 'correct';
      customer_id?: string;
      /* `correct` — what a human read off the document the OCR could not. */
      invoice_text?: string;
      payment_ref?: string;
      payment_date?: string;
      payment_method?: string;
      amount_total?: string;
      payer_name?: string;
      notes?: string;
    }) => adminFetch('admin-remittances', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['remittances'] });
      qc.invalidateQueries({ queryKey: ['remittance'] });
    },
  });
}
